import { ChangeDetectionStrategy, Component, computed, ContentChild, input, output, signal, TemplateRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { catchError, debounceTime, EMPTY, finalize, map, Observable, switchMap } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { NgTemplateOutlet } from '@angular/common';
import { CommonApiListResponse } from '@iot-platform/models/common';

interface SearchParams {
  searchTerm: string;
  currentPage: number;
  retryCount: number;
}

@Component({
  selector: 'iot-platform-ui-paginated-autocomplete',
  imports: [
    ReactiveFormsModule,
    FlexLayoutModule,
    TranslatePipe,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatOptionModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatButtonModule,
    NgTemplateOutlet
  ],
  templateUrl: './paginated-autocomplete.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaginatedAutocompleteComponent<T> {
  searchFn = input.required<(term: string, page: number) => Observable<CommonApiListResponse<T>>>();
  displayFn = input<(item: T) => string>((item: any) => item?.name ?? '');
  searchByFn = input<(item: T) => string>((item: any) => item?.name);
  trackBy = input<(item: T) => unknown>((item: any) => item?.id ?? item);
  getItemDescription = input<((item: T) => string) | null>(null);
  debounceMs = input<number>(500);
  placeholder = input<string>('Search term');
  emptyMessage = input<string>('No items found');
  errorMessage = input<string>('Failed to load items');
  loadMoreMessage = input<string>('Load More');
  disabled = input<boolean>(false);

  selected = output<T | null>();

  items = signal<T[]>([]);
  selectedItem = signal<T | null>(null);
  isLoading = signal<boolean>(false);
  hasMore = signal<boolean>(false);
  hasError = signal<boolean>(false);
  total = signal<number>(0);
  searchParams = signal<SearchParams>({ searchTerm: '', currentPage: 0, retryCount: 0 });

  isEmpty = computed(() => this.items()?.length === 0 && !this.isLoading());
  hasClearButton = computed(() => this.searchParams()?.searchTerm.length > 0 && !this.isLoading());

  @ContentChild('itemTemplate') itemTemplate: TemplateRef<{ $implicit: T }> | null = null;

  constructor() {
    toObservable(this.searchParams)
      .pipe(
        debounceTime(this.debounceMs()),
        switchMap(({ searchTerm, currentPage }) => {
          this.isLoading.set(true);
          this.hasError.set(false);
          return this.searchFn()(searchTerm, currentPage).pipe(
            finalize(() => this.isLoading.set(false)),
            catchError(() => {
              this.items.set([]);
              this.hasMore.set(false);
              this.hasError.set(true);
              return EMPTY;
            })
          );
        }),
        map((result: CommonApiListResponse<T>): CommonApiListResponse<T> => {
          const page = this.selectedItem() ? { ...result.page, hasMore: false } : result.page;
          const content = this.selectedItem()
            ? result.content.filter((item) => this.trackBy()(item) === this.trackBy()(this.selectedItem() as T))
            : result.content;
          return { page, content };
        }),
        takeUntilDestroyed()
      )
      .subscribe((result: CommonApiListResponse<T>) => {
        this.items.update((items) => (this.searchParams()?.currentPage === 0 ? result.content : [...items, ...result.content]));
        this.hasMore.set(result.page.hasMore);
        this.total.set(result.page.total);
      });
  }

  onSearchTermChange(term: string): void {
    const selected = this.selectedItem();
    if (selected && this.searchByFn()(selected).toLowerCase() !== term.toLowerCase()) {
      this.selectedItem.set(null);
      this.selected.emit(null);
    }
    this.searchParams.update((p) => ({ ...p, searchTerm: term.toLowerCase(), currentPage: 0 }));
  }

  onLoadMore(): void {
    this.searchParams.update((p) => ({ ...p, currentPage: p.currentPage + 1 }));
  }

  onItemSelected(item: T): void {
    this.selectedItem.set(item);
    this.searchParams.update((p) => ({ ...p, searchTerm: this.searchByFn()(item).toLowerCase(), currentPage: 0 }));
    this.selected.emit(item);
  }

  clearSearch(): void {
    this.selectedItem.set(null);
    this.searchParams.update((p) => ({ ...p, searchTerm: '', currentPage: 0 }));
    this.selected.emit(null);
  }

  retry(): void {
    this.searchParams.update((p) => ({ ...p, retryCount: p.retryCount + 1 }));
  }
}
